import { Slider, Switch } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import React, { FC, memo, useCallback } from 'react';
import { Button } from 'shared/ui/Button';
import { InputNumber } from 'shared/ui/InputNumber';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { DEFAULT_MAP_CONFIG } from '../../model/mapConfigSerialization';
import { getImageTransform } from '../../model/selectors/getImageTransform';
import { warehouseMapConstructorActions } from '../../model/slice/warehouseMapConstructorSlice';
import { BackgroundScaleMeasureApi } from '../../utils/hooks/useBackgroundScaleMeasure';

interface ImageTransformPanelProps {
  backgroundScaleMeasure: BackgroundScaleMeasureApi;
}

export const ImageTransformPanel: FC<ImageTransformPanelProps> = memo(({ backgroundScaleMeasure }) => {
  const { t } = useAppTranslation('warehouse-management');
  const dispatch = useAppDispatch();
  const image = useAppSelector(getImageTransform);

  const setImageTransform = useCallback(
    (params: Partial<typeof image>): void => {
      dispatch(warehouseMapConstructorActions.setImageTransform(params));
    },
    [dispatch],
  );

  return (
    <>
      <div className="flex items-center space-x-2.5">
        <InputNumber
          className="!h-8 flex-1"
          prefix={t('X')}
          value={roundNumber(image.x, 0)}
          onChange={(value) => {
            if (value !== null) setImageTransform({ x: value });
          }}
        />
        <InputNumber
          className="!h-8 flex-1"
          prefix={t('Y')}
          value={roundNumber(image.y, 0)}
          onChange={(value) => {
            if (value !== null) setImageTransform({ y: value });
          }}
        />
      </div>
      <InputNumber
        className="!h-8"
        prefix={t('Rotation')}
        value={roundNumber(image.rotation, 2)}
        onChange={(value) => {
          if (value !== null) setImageTransform({ rotation: value });
        }}
      />
      <div>
        <div className="mb-1 text-sm text-primaryLight">{t('Opacity')}</div>
        <Slider
          min={0}
          max={100}
          value={roundNumber(image.opacity * 100, 0)}
          onChange={(value) => {
            setImageTransform({ opacity: value / 100 });
          }}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-primaryLight">{t('Lock background')}</span>
        <Switch
          checked={image.locked}
          onChange={(checked) => {
            setImageTransform({ locked: checked });
          }}
        />
      </div>
      <Button
        containerClassName="mt-3"
        theme="secondary"
        onClick={() => {
          setImageTransform(DEFAULT_MAP_CONFIG.image);
        }}
      >
        {t('Reset transform')}
      </Button>
      <div className="mt-5 pt-4 border-t border-secondaryAccent space-y-2.5">
        <div className="text-sm text-primaryLight">{t('Background scale helper')}</div>
        <div className="text-xs text-primaryLight">
          {t('Draw a line over a known plan length to calculate how much to resize the source image externally.')}
        </div>
        <div className="flex items-center gap-2">
          <Button theme="secondary" className="flex-1" onClick={backgroundScaleMeasure.startMeasure}>
            {backgroundScaleMeasure.phase === 'drawing' || backgroundScaleMeasure.phase === 'waitingFirst'
              ? t('Measuring...')
              : t('Measure line')}
          </Button>
          <Button theme="clear" onClick={backgroundScaleMeasure.clearMeasure}>
            {t('Clear')}
          </Button>
        </div>
        <InputNumber
          className="!h-8"
          prefix={t('Length m')}
          min={0.001}
          value={backgroundScaleMeasure.referenceMeters ?? undefined}
          onChange={backgroundScaleMeasure.setReferenceMeters}
        />
        {backgroundScaleMeasure.measuredPixels !== null && (
          <div className="text-xs text-primaryLight">
            {t('Measured')}: {roundNumber(backgroundScaleMeasure.measuredPixels, 1)} px
          </div>
        )}
        {backgroundScaleMeasure.result && (
          <div className="rounded-lg bg-secondaryAccent p-3 text-sm text-primary space-y-1">
            <div>
              {t('Expected')}: {roundNumber(backgroundScaleMeasure.result.targetPixels, 1)} px
            </div>
            <div>
              {t('Resize source image')}: {roundNumber(backgroundScaleMeasure.result.multiplier, 4)}x (
              {roundNumber(backgroundScaleMeasure.result.percentage, 1)}%)
            </div>
          </div>
        )}
      </div>
    </>
  );
});
